import React, { useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { uploadImage } from '../utils/cloudflare';
import { platformLabel as brandLabel } from '../utils/platformIcons';
import ImageCropModal from './ImageCropModal';

// Campaign images are shown through an `object-cover` frame on the card and in
// the detail gallery, so a portrait photo used to arrive as a random middle
// band of itself. Everything is cropped to the card's shape on the way in
// instead, and the brand chooses which band that is.
export const CAMPAIGN_IMAGE_ASPECT = 16 / 9;

/**
 * Every platform a campaign can name.
 *
 * This used to be six hard-coded rows rendered as chips, which quietly decided
 * that a campaign could only run on Instagram, TikTok, YouTube, X, Twitch or
 * LinkedIn. The list below is the real one — and at this length chips no longer
 * fit anywhere, least of all on a phone, so both places that pick a platform
 * (the basics section and each deliverable row) are dropdowns instead.
 *
 * Labels come from `utils/platformIcons`, the same table the profile draws its
 * brand marks from, so a platform is named identically wherever it appears and
 * a new one is added in exactly one place. The last group is the exception:
 * podcasts, blogs and newsletters are not brands, so they carry their own
 * labels and simply fall through `platformLabel`'s capitalise path elsewhere.
 */
const PLATFORM_GROUP_DEFS = [
    { key: 'social', keys: ['instagram', 'tiktok', 'youtube', 'x', 'threads', 'facebook', 'snapchat', 'pinterest', 'linkedin', 'vk'] },
    { key: 'live', keys: ['twitch', 'kick', 'discord', 'telegram', 'whatsapp', 'reddit', 'mastodon'] },
    { key: 'audio', keys: ['spotify', 'apple', 'soundcloud'] },
    { key: 'video', keys: ['vimeo'] },
    { key: 'writing', keys: ['medium', 'substack', 'wordpress', 'tumblr', 'behance', 'dribbble', 'deviantart', 'flickr', 'github'] },
    { key: 'commerce', keys: ['patreon', 'kofi', 'linktree', 'etsy', 'shopier', 'trendyol', 'hepsiburada'] },
    { key: 'other', keys: ['podcast', 'blog', 'newsletter'] },
];

/** X is stored as `x` but half the market still calls it Twitter. */
const LABEL_OVERRIDES = {
    x: 'X (Twitter)',
    podcast: 'Podcast',
    blog: 'Blog',
    newsletter: 'Newsletter',
};

export const PLATFORM_GROUPS = PLATFORM_GROUP_DEFS.map(group => ({
    key: group.key,
    options: group.keys.map(key => ({ key, label: LABEL_OVERRIDES[key] || brandLabel(key) })),
}));

/** Flat, in menu order. */
export const PLATFORMS = PLATFORM_GROUPS.flatMap(group => group.options);

const PLATFORM_LABELS = Object.fromEntries(PLATFORMS.map(p => [p.key, p.label]));

/** Falls back through the brand table, then to the raw value: a campaign saved
 *  before a platform was dropped from the list still has to render its name. */
export const platformLabel = (key) => PLATFORM_LABELS[key] || brandLabel(key) || key;

/**
 * The `<optgroup>` body of every platform dropdown in the product. Shared so
 * the filter on the showcase, the basics picker and a deliverable row can never
 * offer three different lists.
 */
export function PlatformOptions({ t }) {
    return PLATFORM_GROUPS.map(group => (
        <optgroup key={group.key} label={t(`campaigns.platformGroup_${group.key}`)}>
            {group.options.map(option => (
                <option key={option.key} value={option.key}>{option.label}</option>
            ))}
        </optgroup>
    ));
}

export const PAYMENT_MODELS = ['fixed', 'affiliate', 'barter', 'hybrid'];

export const CONTENT_FORMATS = ['reels', 'story', 'post', 'short', 'video', 'live', 'stream'];

// Starting points for the brief — the brand can rename, reorder or delete
// any of them, and add as many of its own as it likes.
const SECTION_PRESETS = [
    { key: 'brief', titleKey: 'campaigns.sectionBrief' },
    { key: 'goal', titleKey: 'campaigns.sectionGoal' },
    { key: 'kpi', titleKey: 'campaigns.sectionKpi' },
    { key: 'tone', titleKey: 'campaigns.sectionTone' },
    { key: 'restrictions', titleKey: 'campaigns.sectionRestrictions' },
    { key: 'deliverables', titleKey: 'campaigns.sectionDeliverables' },
    { key: 'timeline', titleKey: 'campaigns.sectionTimeline' },
    { key: 'rights', titleKey: 'campaigns.sectionRights' },
];

const uid = () => Math.random().toString(36).slice(2, 10);

function Fieldset({ title, hint, children }) {
    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <h3 className="text-sm font-bold text-slate-800">{title}</h3>
            {hint && <p className="text-xs text-slate-400 mt-0.5 mb-3 leading-relaxed">{hint}</p>}
            <div className={hint ? '' : 'mt-3'}>{children}</div>
        </section>
    );
}

function Chip({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active
                ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                : 'bg-white text-slate-600 border-slate-200 hover:border-primary-300'
                }`}
        >
            {children}
        </button>
    );
}

function emptyDraft() {
    return {
        title: '',
        summary: '',
        images: [],
        cover_image: '',
        platforms: [],
        sections: [],
        deliverables: [],
        kpis: [],
        payment_model: 'fixed',
        budget_usd: 0,
        commission_percent: 0,
        barter_value: '',
        payment_terms_days: 14,
        slots: 1,
        revision_limit: 2,
        auto_approve_hours: 72,
        application_deadline: '',
        draft_due_date: '',
        publish_date: '',
        usage_rights_months: 0,
        exclusivity_days: 0,
        exclusivity_note: '',
        utm_link: '',
        discount_code: '',
        min_followers: 0,
        tags: [],
        status: 'active',
    };
}

/**
 * Create / edit modal for a campaign. `campaign` null means "new".
 * Calls `onSaved(campaign)` once the API confirms.
 */
function CampaignForm({ campaign, onClose, onSaved }) {
    const { t } = useTranslation();
    const isEdit = Boolean(campaign);

    const [form, setForm] = useState(() => {
        if (!campaign) return emptyDraft();
        return {
            ...emptyDraft(),
            ...campaign,
            application_deadline: campaign.application_deadline || '',
            draft_due_date: campaign.draft_due_date || '',
            publish_date: campaign.publish_date || '',
            tags: (campaign.tags || []).map(tag => tag.name || tag),
        };
    });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [tagInput, setTagInput] = useState('');
    // Picked files wait here until each has been framed. A multi-select is
    // cropped one at a time rather than all at once — a grid of croppers is
    // unusable on a phone, and the counter says how many are left.
    const [cropQueue, setCropQueue] = useState([]);
    // Held separately from the queue because the queue only ever shrinks — the
    // counter has to keep saying "2 / 5", not "1 / 4".
    const [cropTotal, setCropTotal] = useState(0);

    useEffect(() => { if (!cropQueue.length) setCropTotal(0); }, [cropQueue.length]);

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const handleImages = (event) => {
        const files = Array.from(event.target.files || []);
        // Same input, reset immediately: picking the same file twice in a row
        // fires no change event otherwise.
        event.target.value = '';
        if (!files.length) return;
        setError('');
        setCropQueue(prev => [...prev, ...files]);
        setCropTotal(prev => prev + files.length);
    };

    // Uploads happen per crop, so a long queue fills the strip as it goes
    // rather than after the last one. The sheet stays up, disabled, while the
    // image on screen is the one in flight.
    const handleCropped = async (file) => {
        setUploading(true);
        try {
            const url = await uploadImage(file);
            if (url) {
                setForm(prev => ({
                    ...prev,
                    images: [...prev.images, { id: uid(), url }],
                    cover_image: prev.cover_image || url,
                }));
            } else {
                setError(t('campaigns.uploadError'));
            }
        } catch (err) {
            setError(t('campaigns.uploadError'));
        } finally {
            setUploading(false);
            setCropQueue(prev => prev.slice(1));
        }
    };

    const skipCrop = () => setCropQueue(prev => prev.slice(1));

    const removeImage = (id) => setForm(prev => {
        const images = prev.images.filter(image => image.id !== id);
        const removed = prev.images.find(image => image.id === id);
        return {
            ...prev,
            images,
            cover_image: prev.cover_image === removed?.url ? (images[0]?.url || '') : prev.cover_image,
        };
    });

    const addSection = (preset) => setForm(prev => ({
        ...prev,
        sections: [...prev.sections, {
            id: uid(),
            title: preset ? t(preset.titleKey) : '',
            body: '',
        }],
    }));

    const updateSection = (id, key, value) => setForm(prev => ({
        ...prev,
        sections: prev.sections.map(s => (s.id === id ? { ...s, [key]: value } : s)),
    }));

    const moveSection = (index, direction) => setForm(prev => {
        const sections = [...prev.sections];
        const target = index + direction;
        if (target < 0 || target >= sections.length) return prev;
        [sections[index], sections[target]] = [sections[target], sections[index]];
        return { ...prev, sections };
    });

    const addRow = (key, row) => setForm(prev => ({ ...prev, [key]: [...prev[key], { id: uid(), ...row }] }));
    const updateRow = (key, id, field, value) => setForm(prev => ({
        ...prev,
        [key]: prev[key].map(row => (row.id === id ? { ...row, [field]: value } : row)),
    }));
    const removeRow = (key, id) => setForm(prev => ({
        ...prev, [key]: prev[key].filter(row => row.id !== id),
    }));

    const togglePlatform = (key) => setForm(prev => ({
        ...prev,
        platforms: prev.platforms.includes(key)
            ? prev.platforms.filter(p => p !== key)
            : [...prev.platforms, key],
    }));

    const addTag = () => {
        const value = tagInput.trim();
        if (!value || form.tags.includes(value)) return;
        set('tags', [...form.tags, value]);
        setTagInput('');
    };

    const handleSubmit = async (event, status) => {
        event.preventDefault();
        if (!form.title.trim()) {
            setError(t('campaigns.titleRequired'));
            return;
        }
        setSaving(true);
        setError('');
        const payload = { ...form, status: status || form.status };
        try {
            const res = isEdit
                ? await api.patch(`/api/campaigns/${campaign.id}/`, payload)
                : await api.post('/api/campaigns/', payload);
            if (res.status === 200 || res.status === 201) {
                onSaved(res.data);
            }
        } catch (err) {
            setError(err.response?.data?.error || t('campaigns.saveError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9000] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-slate-50 w-full sm:max-w-3xl h-[92vh] sm:h-[88vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-slate-100 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-black text-slate-800">
                            {isEdit ? t('campaigns.editTitle') : t('campaigns.newTitle')}
                        </h2>
                        <p className="text-xs text-slate-400">{t('campaigns.formHint')}</p>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={(e) => handleSubmit(e)} className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-5 py-4 space-y-4">

                    <Fieldset title={t('campaigns.basicsTitle')} hint={t('campaigns.basicsHint')}>
                        <label className="label">{t('campaigns.fieldTitle')}</label>
                        <input className="input" value={form.title} maxLength={140}
                            onChange={(e) => set('title', e.target.value)}
                            placeholder={t('campaigns.titlePlaceholder')} />

                        <label className="label mt-3">{t('campaigns.fieldSummary')}</label>
                        <input className="input" value={form.summary} maxLength={300}
                            onChange={(e) => set('summary', e.target.value)}
                            placeholder={t('campaigns.summaryPlaceholder')} />

                        <label className="label mt-4">{t('campaigns.fieldImages')}</label>
                        {/* Thumbnails carry the card's shape, not a square:
                            a square preview of a 16:9 crop is the same lie the
                            cropper was added to remove. */}
                        <div className="flex flex-wrap gap-2">
                            {form.images.map(image => (
                                <div key={image.id} className="relative w-28 aspect-[16/9] rounded-xl overflow-hidden border border-slate-200 group">
                                    <img src={image.url} alt="" className="w-full h-full object-cover" />
                                    <button type="button" onClick={() => removeImage(image.id)}
                                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs flex items-center justify-center">×</button>
                                    <button type="button" onClick={() => set('cover_image', image.url)}
                                        className={`absolute bottom-0 inset-x-0 text-[9px] font-bold py-0.5 transition ${form.cover_image === image.url
                                            ? 'bg-primary-600 text-white'
                                            : 'bg-black/50 text-white opacity-0 group-hover:opacity-100'}`}>
                                        {form.cover_image === image.url ? t('campaigns.cover') : t('campaigns.makeCover')}
                                    </button>
                                </div>
                            ))}
                            <label className="w-28 aspect-[16/9] rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-primary-400 transition text-slate-400">
                                {uploading
                                    ? <span className="w-4 h-4 border-2 border-slate-300 border-t-primary-600 rounded-full animate-spin" />
                                    : <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                    </svg>}
                                <input type="file" accept="image/*" multiple className="hidden" onChange={handleImages} />
                            </label>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">{t('campaigns.imagesHint')}</p>

                        {/* Picked from a dropdown, shown back as chips. The
                            full platform list is far too long to render as a
                            chip grid, but the *selection* is short — so the
                            menu does the choosing and the chips do the
                            remembering. Already-picked entries are dropped
                            from the menu rather than disabled, so it shrinks
                            as the answer grows. */}
                        <label className="label mt-4">{t('campaigns.fieldPlatforms')}</label>
                        <select className="input py-2 text-sm" value=""
                            onChange={(e) => { if (e.target.value) togglePlatform(e.target.value); }}>
                            <option value="">{t('campaigns.addPlatform')}</option>
                            {PLATFORM_GROUPS.map(group => {
                                const options = group.options.filter(o => !form.platforms.includes(o.key));
                                if (!options.length) return null;
                                return (
                                    <optgroup key={group.key} label={t(`campaigns.platformGroup_${group.key}`)}>
                                        {options.map(option => (
                                            <option key={option.key} value={option.key}>{option.label}</option>
                                        ))}
                                    </optgroup>
                                );
                            })}
                        </select>
                        {form.platforms.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {form.platforms.map(key => (
                                    <span key={key}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary-600 text-white shadow-sm">
                                        {platformLabel(key)}
                                        <button type="button" onClick={() => togglePlatform(key)}
                                            className="text-white/70 hover:text-white font-bold leading-none">×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </Fieldset>

                    {/* Free-form brief */}
                    <Fieldset title={t('campaigns.briefTitle')} hint={t('campaigns.briefHint')}>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {SECTION_PRESETS.map(preset => (
                                <button key={preset.key} type="button" onClick={() => addSection(preset)}
                                    className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-primary-50 text-primary-700 border border-primary-100 hover:bg-primary-100 transition">
                                    + {t(preset.titleKey)}
                                </button>
                            ))}
                            <button type="button" onClick={() => addSection(null)}
                                className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition">
                                + {t('campaigns.customSection')}
                            </button>
                        </div>

                        {form.sections.length === 0 && (
                            <p className="text-xs text-slate-400 italic py-2">{t('campaigns.noSections')}</p>
                        )}

                        <div className="space-y-3">
                            {form.sections.map((section, index) => (
                                <div key={section.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <input
                                            className="flex-1 bg-transparent text-base font-bold text-slate-800 placeholder-slate-300 focus:outline-none"
                                            value={section.title}
                                            placeholder={t('campaigns.sectionTitlePlaceholder')}
                                            maxLength={80}
                                            onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                                        />
                                        <button type="button" onClick={() => moveSection(index, -1)} className="text-slate-400 hover:text-slate-700 px-1" title="↑">↑</button>
                                        <button type="button" onClick={() => moveSection(index, 1)} className="text-slate-400 hover:text-slate-700 px-1" title="↓">↓</button>
                                        <button type="button" onClick={() => removeRow('sections', section.id)} className="text-red-400 hover:text-red-600 px-1">×</button>
                                    </div>
                                    <textarea
                                        className="input min-h-[80px] resize-y bg-white text-sm"
                                        value={section.body}
                                        placeholder={t('campaigns.sectionBodyPlaceholder')}
                                        onChange={(e) => updateSection(section.id, 'body', e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </Fieldset>

                    {/* Deliverables */}
                    <Fieldset title={t('campaigns.deliverablesTitle')} hint={t('campaigns.deliverablesHint')}>
                        <div className="space-y-2">
                            {form.deliverables.map(row => (
                                <div key={row.id} className="flex flex-wrap items-center gap-2">
                                    <select className="input flex-1 min-w-[110px] py-2 text-sm" value={row.platform || ''}
                                        onChange={(e) => updateRow('deliverables', row.id, 'platform', e.target.value)}>
                                        <option value="">{t('campaigns.platform')}</option>
                                        <PlatformOptions t={t} />
                                    </select>
                                    <select className="input flex-1 min-w-[110px] py-2 text-sm" value={row.format || ''}
                                        onChange={(e) => updateRow('deliverables', row.id, 'format', e.target.value)}>
                                        <option value="">{t('campaigns.format')}</option>
                                        {CONTENT_FORMATS.map(f => <option key={f} value={f}>{t(`campaigns.format_${f}`)}</option>)}
                                    </select>
                                    <input type="number" min={1} className="input w-20 py-2 text-sm" value={row.quantity || 1}
                                        onChange={(e) => updateRow('deliverables', row.id, 'quantity', e.target.value)} />
                                    <button type="button" onClick={() => removeRow('deliverables', row.id)}
                                        className="text-red-400 hover:text-red-600 px-1">×</button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={() => addRow('deliverables', { platform: '', format: '', quantity: 1 })}
                            className="btn-secondary text-xs mt-3 py-1.5 px-3">+ {t('campaigns.addDeliverable')}</button>
                    </Fieldset>

                    {/* KPIs */}
                    <Fieldset title={t('campaigns.kpiTitle')} hint={t('campaigns.kpiHint')}>
                        <div className="space-y-2">
                            {form.kpis.map(row => (
                                <div key={row.id} className="flex items-center gap-2">
                                    <input className="input flex-1 py-2 text-sm" value={row.metric || ''}
                                        placeholder={t('campaigns.kpiMetricPlaceholder')}
                                        onChange={(e) => updateRow('kpis', row.id, 'metric', e.target.value)} />
                                    <input className="input w-28 py-2 text-sm" value={row.target || ''}
                                        placeholder={t('campaigns.kpiTargetPlaceholder')}
                                        onChange={(e) => updateRow('kpis', row.id, 'target', e.target.value)} />
                                    <button type="button" onClick={() => removeRow('kpis', row.id)}
                                        className="text-red-400 hover:text-red-600 px-1">×</button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={() => addRow('kpis', { metric: '', target: '' })}
                            className="btn-secondary text-xs mt-3 py-1.5 px-3">+ {t('campaigns.addKpi')}</button>
                    </Fieldset>

                    {/* Budget */}
                    <Fieldset title={t('campaigns.budgetTitle')} hint={t('campaigns.budgetHint')}>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {PAYMENT_MODELS.map(model => (
                                <Chip key={model} active={form.payment_model === model}
                                    onClick={() => set('payment_model', model)}>
                                    {t(`campaigns.payment_${model}`)}
                                </Chip>
                            ))}
                        </div>
                        <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                            {t(`campaigns.paymentDesc_${form.payment_model}`)}
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {(form.payment_model === 'fixed' || form.payment_model === 'hybrid') && (
                                <div>
                                    <label className="label">{t('campaigns.fieldBudgetUsd')}</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">$</span>
                                        <input type="number" min={0} step="0.01" className="input pl-7"
                                            value={form.budget_usd}
                                            onChange={(e) => set('budget_usd', e.target.value)} />
                                    </div>
                                    <p className="text-[11px] text-slate-400 mt-1">{t('campaigns.escrowNote')}</p>
                                </div>
                            )}
                            {(form.payment_model === 'affiliate' || form.payment_model === 'hybrid') && (
                                <div>
                                    <label className="label">{t('campaigns.fieldCommission')}</label>
                                    <input type="number" min={0} max={100} className="input" value={form.commission_percent}
                                        onChange={(e) => set('commission_percent', e.target.value)} />
                                </div>
                            )}
                            {(form.payment_model === 'barter' || form.payment_model === 'hybrid') && (
                                <div className="sm:col-span-2">
                                    <label className="label">{t('campaigns.fieldBarter')}</label>
                                    <input className="input" value={form.barter_value}
                                        placeholder={t('campaigns.barterPlaceholder')}
                                        onChange={(e) => set('barter_value', e.target.value)} />
                                </div>
                            )}
                            <div>
                                <label className="label">{t('campaigns.fieldPaymentTerms')}</label>
                                <input type="number" min={0} className="input" value={form.payment_terms_days}
                                    onChange={(e) => set('payment_terms_days', e.target.value)} />
                            </div>
                            <div>
                                <label className="label">{t('campaigns.fieldSlots')}</label>
                                <input type="number" min={1} className="input" value={form.slots}
                                    onChange={(e) => set('slots', e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>

                    {/* Timeline & rules */}
                    <Fieldset title={t('campaigns.timelineTitle')} hint={t('campaigns.timelineHint')}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="label">{t('campaigns.fieldApplicationDeadline')}</label>
                                <input type="date" className="input" value={form.application_deadline || ''}
                                    onChange={(e) => set('application_deadline', e.target.value)} />
                            </div>
                            <div>
                                <label className="label">{t('campaigns.fieldDraftDue')}</label>
                                <input type="date" className="input" value={form.draft_due_date || ''}
                                    onChange={(e) => set('draft_due_date', e.target.value)} />
                            </div>
                            <div>
                                <label className="label">{t('campaigns.fieldPublishDate')}</label>
                                <input type="date" className="input" value={form.publish_date || ''}
                                    onChange={(e) => set('publish_date', e.target.value)} />
                            </div>
                            <div>
                                <label className="label">{t('campaigns.fieldRevisionLimit')}</label>
                                <input type="number" min={0} max={5} className="input" value={form.revision_limit}
                                    onChange={(e) => set('revision_limit', e.target.value)} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="label">{t('campaigns.fieldAutoApprove')}</label>
                                <input type="number" min={24} max={240} className="input" value={form.auto_approve_hours}
                                    onChange={(e) => set('auto_approve_hours', e.target.value)} />
                                <p className="text-[11px] text-slate-400 mt-1">{t('campaigns.autoApproveNote')}</p>
                            </div>
                        </div>
                    </Fieldset>

                    {/* Rights */}
                    <Fieldset title={t('campaigns.rightsTitle')} hint={t('campaigns.rightsHint')}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="label">{t('campaigns.fieldUsageRights')}</label>
                                <input type="number" min={0} className="input" value={form.usage_rights_months}
                                    onChange={(e) => set('usage_rights_months', e.target.value)} />
                            </div>
                            <div>
                                <label className="label">{t('campaigns.fieldExclusivity')}</label>
                                <input type="number" min={0} className="input" value={form.exclusivity_days}
                                    onChange={(e) => set('exclusivity_days', e.target.value)} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="label">{t('campaigns.fieldExclusivityNote')}</label>
                                <input className="input" value={form.exclusivity_note} maxLength={255}
                                    placeholder={t('campaigns.exclusivityPlaceholder')}
                                    onChange={(e) => set('exclusivity_note', e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>

                    {/* Tracking & targeting */}
                    <Fieldset title={t('campaigns.trackingTitle')} hint={t('campaigns.trackingHint')}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="label">{t('campaigns.fieldUtm')}</label>
                                <input className="input" value={form.utm_link || ''} placeholder="https://..."
                                    onChange={(e) => set('utm_link', e.target.value)} />
                            </div>
                            <div>
                                <label className="label">{t('campaigns.fieldDiscountCode')}</label>
                                <input className="input" value={form.discount_code} maxLength={64}
                                    onChange={(e) => set('discount_code', e.target.value)} />
                            </div>
                            <div>
                                <label className="label">{t('campaigns.fieldMinFollowers')}</label>
                                <input type="number" min={0} className="input" value={form.min_followers}
                                    onChange={(e) => set('min_followers', e.target.value)} />
                            </div>
                            <div>
                                <label className="label">{t('campaigns.fieldTags')}</label>
                                <div className="flex gap-2">
                                    <input className="input" value={tagInput} maxLength={30}
                                        placeholder={t('campaigns.tagPlaceholder')}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
                                    <button type="button" onClick={addTag} className="btn-secondary px-3 text-sm">+</button>
                                </div>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {form.tags.map(tag => (
                                        <span key={tag} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold flex items-center gap-1">
                                            {tag}
                                            <button type="button" onClick={() => set('tags', form.tags.filter(x => x !== tag))}
                                                className="text-slate-400 hover:text-red-500">×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Fieldset>

                    {error && (
                        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">{error}</p>
                    )}
                </form>

                {/* Footer */}
                <div className="flex items-center gap-3 px-5 py-4 bg-white border-t border-slate-100 flex-shrink-0">
                    <button type="button" onClick={onClose} className="btn-secondary flex-1 py-2.5 text-sm">
                        {t('campaigns.cancel')}
                    </button>
                    <button type="button" disabled={saving} onClick={(e) => handleSubmit(e, 'draft')}
                        className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition disabled:opacity-50">
                        {t('campaigns.saveDraft')}
                    </button>
                    <button type="button" disabled={saving} onClick={(e) => handleSubmit(e, 'active')}
                        className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2">
                        {saving && <span className="spinner-sm" />}
                        {t('campaigns.publish')}
                    </button>
                </div>
            </div>

            {/* Sits above the form's own layer (Modal offsets its z-index by
                depth), so the sheet is reachable while the form stays put. */}
            <ImageCropModal
                file={cropQueue[0] || null}
                aspect={CAMPAIGN_IMAGE_ASPECT}
                busy={uploading}
                counter={cropTotal > 1 ? `${cropTotal - cropQueue.length + 1} / ${cropTotal}` : null}
                cancelLabel={cropQueue.length > 1 ? t('imageCrop.skip') : t('campaigns.cancel')}
                onCancel={skipCrop}
                onCropped={handleCropped}
            />
        </div>
    );
}

export default CampaignForm;
